import translate from '@allesauto/i18n';
import { Tab } from '@headlessui/react';
import clsx from 'clsx';
import * as React from 'react';
import { useIntl } from 'react-intl';

import { featureflags } from '@/global/featureflags/featureflags';

import AccessoriesForm from './AccessoriesForm/AccessoriesForm';
import ServiceBookingForm from './ServiceBookingForm/ServiceBookingForm';
import VehicleSearchForm, {
  VehicleSearchFormProps,
} from './VehicleSearchForm/VehicleSearchForm';

export interface QuickEntryWidgetProps {
  defaultSelectedTabIndex?: number;
  vehicleSearchForm: VehicleSearchFormProps;
}

type OnChange = React.FormEventHandler<HTMLDivElement> &
  ((index: number) => void);

export default function QuickEntryWidget({
  defaultSelectedTabIndex = 0,
  vehicleSearchForm,
}: QuickEntryWidgetProps) {
  const intl = useIntl();

  const [defaultIndex, setDefaultIndex] = React.useState(
    Number(defaultSelectedTabIndex)
  );
  const hasAccessoriesShop = featureflags.accessoriesShopEnabled;

  const COMMON_TABS = [
    translate(intl, 'ui.QUICK_ENTRY_WIDGET.VEHICLE_SEARCH_FORM.TAB'),
    translate(intl, 'ui.QUICK_ENTRY_WIDGET.SERVICE_FORM.TAB'),
  ];
  const TABS = hasAccessoriesShop
    ? [
        ...COMMON_TABS,
        translate(intl, 'ui.QUICK_ENTRY_WIDGET.ACCESORIES_WIDGET.TAB'),
      ]
    : COMMON_TABS;

  const className = {
    wrapper: 'relative w-screen lg:w-[480px]',
    wrapperInner:
      'hidden lg:block -top-30px absolute border-7px border-green60 monochrome:border-white wl-border-highlight h-full left-[15px] w-full z-0 lg:-left-30px lg:top-30px',
    tabs: 'relative z-10',
    tabList: 'bg-purple80 flex h-[50px] text-p1 text-white w-full',
    tab: 'flex flex-1 font-regular font-weight-bold items-center justify-center',
    panel: 'bg-white p-15px lg:p-30px',
  };

  React.useEffect(() => {
    const DEFAULT_INDEX =
      Number(defaultSelectedTabIndex) + 1 > TABS.length
        ? TABS.length - 1
        : Number(defaultSelectedTabIndex);

    setDefaultIndex(DEFAULT_INDEX);
  }, [defaultSelectedTabIndex, TABS.length]);

  const onChange: OnChange = (index) => setDefaultIndex(index as number);

  return (
    <div className={className.wrapper}>
      <div className={className.wrapperInner}></div>
      <Tab.Group
        as="div"
        className={className.tabs}
        selectedIndex={defaultIndex}
        onChange={onChange}
        manual
      >
        <Tab.List className={className.tabList}>
          {TABS.map((t, i) => (
            <Tab
              key={t}
              className={({ selected }) =>
                clsx(
                  className.tab,
                  selected
                    ? 'bg-white text-purple80 monochrome:text-black outline-none'
                    : 'bg-purple80 monochrome:bg-grey90 !text-white outline-none',
                  i != 0 && 'border-l-1px border-l-white'
                )
              }
            >
              {t}
            </Tab>
          ))}
        </Tab.List>
        <Tab.Panels>
          <Tab.Panel className={className.panel}>
            <VehicleSearchForm {...vehicleSearchForm} />
          </Tab.Panel>

          <Tab.Panel className={className.panel}>
            <ServiceBookingForm />
          </Tab.Panel>
          {hasAccessoriesShop && (
            <Tab.Panel className={className.panel}>
              <AccessoriesForm />
            </Tab.Panel>
          )}
        </Tab.Panels>
      </Tab.Group>
    </div>
  );
}
